import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import ModalShell from "../core/modal/ModalShell";

const ModalContext = createContext(null);
export const useModal = () => useContext(ModalContext);

let modalRoot = document.getElementById('modal-root');
if (!modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    document.body.appendChild(modalRoot);
}

export function ModalProvider({children}) {
    /** Stack položek: { id, render, options, resolve } */
    const [stack, setStack] = useState([]);

    // Zakázání scrollu při libovolném otevřeném modálu
    useEffect(() => {
        const prev = document.body.style.overflow;
        if (stack.length > 0) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = prev || '';
        return () => {
            document.body.style.overflow = prev || '';
        };
    }, [stack.length]);

    // ESC zavře vrchní modál (pokud escapable)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && stack.length) {
                const top = stack[stack.length - 1];
                if (top?.options?.escapable !== false) {
                    e.stopPropagation();
                    closeTopModal({dismissed: true});
                }
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [stack]);

    const openModal = useCallback((Component, props = {}, options = {}) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        return new Promise(resolve => {
            const render = (close) => <Component {...props} close={close} modalId={id}/>;
            setStack(s => [...s, {id, render, options, resolve}]);
        });
    }, []);

    const closeModalById = useCallback((id, result) => {
        setStack(s => {
            const idx = s.findIndex(m => m.id === id);
            if (idx === -1) return s;
            const item = s[idx];
            queueMicrotask(() => item.resolve?.(result));
            return s.filter((_, i) => i !== idx);
        });
    }, []);

    const closeTopModal = useCallback((result) => {
        setStack(s => {
            if (!s.length) return s;
            const top = s[s.length - 1];
            queueMicrotask(() => top.resolve?.(result));
            return s.slice(0, -1);
        });
    }, []);

    // Vestavěné pomocné modály (alert/confirm/prompt)
    const alert = useCallback((message, {title = 'Upozornění', okText = 'OK'} = {}) =>
        openModal(({close}) => (
            <>
                <p style={{whiteSpace: 'pre-wrap', margin: 0}}>{message}</p>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16}}>
                    <button onClick={() => close(true)}>{okText}</button>
                </div>
            </>
        ), {}, {title}), [openModal]
    );

    const confirm = useCallback((message, {
            title = 'Potvrdit', okText = 'Ano', cancelText = 'Ne'
        } = {}) =>
            openModal(({close}) => (
                <>
                    <p style={{whiteSpace: 'pre-wrap', margin: 0}}>{message}</p>
                    <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16}}>
                        <button onClick={() => close(false)}>{cancelText}</button>
                        <button onClick={() => close(true)}>{okText}</button>
                    </div>
                </>
            ), {}, {title, backdropClosable: false}), [openModal]
    );

    function PromptModal({message, defaultValue, okText, cancelText, close}) {
        const [val, setVal] = React.useState(defaultValue || '');

        return (
            <>
                <label style={{display: 'block', marginBottom: 8}}>{message}</label>
                <input autoFocus value={val} onChange={e => setVal(e.target.value)} style={{width: '100%'}}/>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16}}>
                    <button onClick={() => close(null)}>{cancelText}</button>
                    <button onClick={() => close(val)}>{okText}</button>
                </div>
            </>
        );
    }

    const prompt = useCallback((message, {
            title = 'Zadat hodnotu',
            okText = 'OK',
            cancelText = 'Zrušit',
            defaultValue = ''
        } = {}) =>
            openModal(PromptModal, {message, defaultValue, okText, cancelText}, {title, backdropClosable: false}),
        [openModal]
    );

    const ctx = useMemo(() => ({
        openModal, closeTopModal, closeModalById, alert, confirm, prompt
    }), [openModal, closeTopModal, closeModalById, alert, confirm, prompt]);

    const baseZ = 1000;

    return (
        <ModalContext.Provider value={ctx}>
            {children}
            {createPortal(
                stack.map((item, i) => (
                    <ModalShell
                        key={item.id}
                        isOpen={true}
                        title={item.options?.title}
                        onClose={(res) => closeModalById(item.id, res)}
                        zIndex={baseZ + i * 10}
                        backdropClosable={item.options?.backdropClosable !== false}
                    >
                        {item.render((res) => closeModalById(item.id, res))}
                    </ModalShell>
                )),
                modalRoot
            )}        </ModalContext.Provider>
    );
}
